import { DateTime, Interval } from 'luxon';
import { LruObjectCache, createCachedSelector } from 're-reselect';
import 'reselect';

// Note: Unless specified, all timezones will be in local time (whatever that is for a given user)

let startOfToday: DateTime | null = null;
export const getStartOfToday = (now: DateTime) => {
  const today = now.startOf('day');
  if (startOfToday === null || +startOfToday !== +today) {
    startOfToday = today;
  }
  return startOfToday;
};

let endOfToday: DateTime | null = null;
export const getEndOfToday = (now: DateTime) => {
  const today = now.endOf('day');
  if (endOfToday === null || +endOfToday !== +today) {
    endOfToday = today;
  }
  return endOfToday;
};

export const getStartOfDayFromToday = createCachedSelector(
  getStartOfToday,
  (_now: DateTime, relativeValue: number) => relativeValue,
  (_now, _relativeValue, unit = 'days') => unit,
  (currentDay, relativeValue, unit) => currentDay.plus({ [unit]: relativeValue }),
)((_now, relativeValue, unit = 'days') => `${relativeValue}_${unit}`);

let startOfThisMonth: DateTime | null = null;
export const getStartOfThisMonth = (now: DateTime) => {
  const thisMonth = now.startOf('month');
  if (startOfThisMonth === null || +startOfThisMonth !== +thisMonth) {
    startOfThisMonth = thisMonth;
  }
  return startOfThisMonth;
};

let endOfThisMonth: DateTime | null = null;
export const getEndOfThisMonth = (now: DateTime) => {
  const thisMonth = now.endOf('month');
  if (endOfThisMonth === null || +endOfThisMonth !== +thisMonth) {
    endOfThisMonth = thisMonth;
  }
  return endOfThisMonth;
};

export const getEndOfMonthFromThisMonth = createCachedSelector(
  getEndOfThisMonth,
  (_now: DateTime, relativeMonths: number) => relativeMonths,
  (thisMonth, relativeMonths) => thisMonth.plus({ months: relativeMonths }).endOf('month'),
)((_now, relativeMonths) => relativeMonths);

let startOfQuarter: DateTime | null = null;
export const getStartOfQuarter = (now: DateTime) => {
  const thisQuarter = now.startOf('quarter');
  if (startOfQuarter === null || +startOfQuarter !== +thisQuarter) {
    startOfQuarter = thisQuarter;
  }
  return startOfQuarter;
};

export const getStartOfQuarterFromThisQuarter = createCachedSelector(
  getStartOfQuarter,
  (_now: DateTime, relativeQuarters: number) => relativeQuarters,
  (thisQuarter, relativeQuarters) => thisQuarter.plus({ quarters: relativeQuarters }),
)((_now, relativeQuarters) => relativeQuarters);

let endOfQuarter: DateTime | null = null;
export const getEndOfQuarter = (now = DateTime.local()) => {
  const thisQuarter = now.endOf('quarter');
  if (endOfQuarter === null || +endOfQuarter !== +thisQuarter) {
    endOfQuarter = thisQuarter;
  }
  return endOfQuarter;
};

export const getEndOfQuarterFromThisQuarter = createCachedSelector(
  getEndOfQuarter,
  (_now: DateTime, relativeQuarters: number) => relativeQuarters,
  (thisQuarter, relativeQuarters) => thisQuarter.plus({ quarters: relativeQuarters }).endOf('quarter'),
)((_now, relativeQuarters) => relativeQuarters);

export const getStartOfMonthFromThisMonth = createCachedSelector(
  getStartOfThisMonth,
  (_now: DateTime, relativeMonths: number) => relativeMonths,
  (thisMonth, relativeMonths) => thisMonth.plus({ months: relativeMonths }),
)((_now, relativeMonths) => relativeMonths);

let startOfThisYear: DateTime | null = null;
export const getStartOfThisYear = (now: DateTime) => {
  const thisYear = now.startOf('year');
  if (startOfThisYear === null || +startOfThisYear !== +thisYear) {
    startOfThisYear = thisYear;
  }
  return startOfThisYear;
};

export const getStartOfYearFromThisYear = createCachedSelector(
  getStartOfThisYear,
  (_now: DateTime, relativeYears: number) => relativeYears,
  (thisYear, relativeYears) => thisYear.plus({ years: relativeYears }),
)((_now, relativeYears) => relativeYears);

export const getInterval = createCachedSelector(
  (startDate: DateTime) => startDate,
  (_: any, endDate: DateTime) => endDate,
  (startDate, endDate) =>
    startDate <= endDate ? Interval.fromDateTimes(startDate, endDate) : Interval.fromDateTimes(endDate, startDate),
)({
  keySelector: (startDate, endDate) => `${startDate}_${endDate}`,
  cacheObject: new LruObjectCache({ cacheSize: 50 }),
});

export type PREDEFINED_RANGE_VALUE =
  | 'TODAY'
  | 'MONTH_TO_DATE'
  | 'THIS_MONTH'
  | 'YEAR_TO_DATE'
  | 'LAST_30_DAYS'
  | '30_DAYS'
  | '60_DAYS'
  | '90_DAYS'
  | '365_DAYS'
  | 'NEXT_365_DAYS'
  | 'LAST_2_MONTHS'
  | 'LAST_3_MONTHS'
  | 'LAST_6_MONTHS'
  | 'LAST_12_MONTHS'
  | 'LAST_MONTH'
  | 'LAST_YEAR'
  | 'NEXT_14_DAYS'
  | 'NEXT_MONTH'
  | 'NEXT_30_DAYS'
  | 'NEXT_60_DAYS'
  | 'THIS_QUARTER'
  | 'LAST_QUARTER'
  | 'NEXT_QUARTER'
  | '6_MONTHS'
  | '12_MONTHS'
  | '3_MONTHS_PRIOR'
  | '6_MONTHS_PRIOR'
  | '12_MONTHS_PRIOR'
  | 'QUARTER_TO_DATE';

// These cannot be in flux module!
// importing flux into our utils breaks tree-shaking for QEM! Don't move to generated reports!
export type GeneratedReportRanges =
  | 'ALL_DATES'
  | 'THIS_MONTH'
  | 'THIS_QUARTER'
  | 'THIS_YEAR'
  | 'LAST_MONTH'
  | 'LAST_QUARTER'
  | 'LAST_YEAR'
  | 'MONTH_TO_DATE'
  | 'QUARTER_TO_DATE'
  | 'YEAR_TO_DATE'
  | 'LAST_7_DAYS'
  | 'LAST_14_DAYS'
  | 'LAST_2_MONTHS'
  | 'LAST_3_MONTHS'
  | 'LAST_6_MONTHS'
  | 'LAST_12_MONTHS'
  | 'CUSTOM';

export type Timestep =
  | 'DAY'
  | 'WEEK'
  | 'TWO_WEEKS'
  | 'HALF_MONTH'
  | 'MONTH'
  | 'QUARTER'
  | 'SIX_MONTHS'
  | 'YEAR'
  | 'NONE'
  | 'OVER-TIME';

export type CUSTOM_RANGE_VALUE = 'CUSTOM';

export type PredefinedRange =
  | {
      value: CUSTOM_RANGE_VALUE;
      endDate: DateTime;
      startDate: DateTime;
    }
  | {
      value: PREDEFINED_RANGE_VALUE;
    };

export const convertGeneratedReportDateRangeToPredefinedRange = (
  input: GeneratedReportRanges,
): PREDEFINED_RANGE_VALUE | undefined => {
  switch (input) {
    case 'THIS_MONTH':
    case 'THIS_QUARTER':
    case 'LAST_MONTH':
    case 'LAST_QUARTER':
    case 'LAST_YEAR':
    case 'MONTH_TO_DATE':
    case 'QUARTER_TO_DATE':
    case 'YEAR_TO_DATE':
    case 'LAST_2_MONTHS':
    case 'LAST_3_MONTHS':
    case 'LAST_6_MONTHS':
    case 'LAST_12_MONTHS':
      return input;
  }
  return undefined;
};

export const convertPredefinedRangeToGeneratedReportDateRange = (
  input: PREDEFINED_RANGE_VALUE,
): GeneratedReportRanges | undefined => {
  switch (input) {
    case 'MONTH_TO_DATE':
    case 'THIS_MONTH':
    case 'YEAR_TO_DATE':
    case 'LAST_2_MONTHS':
    case 'LAST_3_MONTHS':
    case 'LAST_6_MONTHS':
    case 'LAST_12_MONTHS':
    case 'LAST_MONTH':
    case 'LAST_YEAR':
    case 'THIS_QUARTER':
    case 'LAST_QUARTER':
    case 'QUARTER_TO_DATE':
      return input;
    case '12_MONTHS':
    case '3_MONTHS_PRIOR':
    case '6_MONTHS_PRIOR':
    case '12_MONTHS_PRIOR':
    case 'NEXT_QUARTER':
    case 'NEXT_14_DAYS':
    case 'NEXT_MONTH':
    case 'NEXT_30_DAYS':
    case 'NEXT_60_DAYS':
    case '60_DAYS':
    case '90_DAYS':
    case '365_DAYS':
    case 'NEXT_365_DAYS':
    case '30_DAYS':
    case 'LAST_30_DAYS':
    case 'TODAY':
    case '6_MONTHS':
      // Up to the consumer to figure out what they want to do here. These values are not supported by the generated reports service.
      return undefined;
  }
};

export const constructPredefinedInterval = (predefinedRange: PredefinedRange, now: DateTime): Interval => {
  switch (predefinedRange.value) {
    case 'TODAY':
      return getInterval(getStartOfToday(now), getEndOfToday(now));
    case 'MONTH_TO_DATE':
      return getInterval(getStartOfThisMonth(now), getStartOfToday(now));
    case 'THIS_MONTH':
      return getInterval(getStartOfThisMonth(now), getEndOfThisMonth(now));
    case 'YEAR_TO_DATE':
      return getInterval(getStartOfThisYear(now), getStartOfToday(now));
    case 'LAST_30_DAYS':
      return getInterval(getStartOfDayFromToday(now, -29), getStartOfToday(now));
    case '30_DAYS':
      return getInterval(getStartOfDayFromToday(now, -30), getStartOfToday(now));
    case '60_DAYS':
      return getInterval(getStartOfDayFromToday(now, -60), getStartOfToday(now));
    case '90_DAYS':
      return getInterval(getStartOfDayFromToday(now, -90), getStartOfToday(now));
    case '365_DAYS':
      return getInterval(getStartOfDayFromToday(now, -365), getStartOfToday(now));
    case 'NEXT_365_DAYS':
      return getInterval(getStartOfToday(now), getStartOfDayFromToday(now, 365));
    case 'LAST_2_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -1), getStartOfToday(now));
    case 'LAST_3_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -2), getStartOfToday(now));
    case 'LAST_6_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -5), getStartOfToday(now));
    case 'LAST_12_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -11), getStartOfToday(now));
    case 'LAST_MONTH':
      return getInterval(getStartOfMonthFromThisMonth(now, -1), getStartOfThisMonth(now).minus({ days: 1 }));
    case 'LAST_YEAR':
      return getInterval(getStartOfYearFromThisYear(now, -1), getStartOfThisYear(now).minus({ days: 1 }));
    case 'NEXT_14_DAYS':
      return getInterval(getStartOfToday(now), getStartOfDayFromToday(now, 14));
    case 'NEXT_MONTH':
      return getInterval(getStartOfMonthFromThisMonth(now, 1), getEndOfMonthFromThisMonth(now, 1));
    case 'NEXT_30_DAYS':
      return getInterval(getStartOfToday(now), getStartOfDayFromToday(now, 30));
    case 'NEXT_60_DAYS':
      return getInterval(getStartOfToday(now), getStartOfDayFromToday(now, 60));
    case 'THIS_QUARTER':
      return getInterval(getStartOfQuarter(now), getEndOfQuarter());
    case 'LAST_QUARTER':
      return getInterval(getStartOfQuarterFromThisQuarter(now, -1), getEndOfQuarterFromThisQuarter(now, -1));
    case 'NEXT_QUARTER':
      return getInterval(getStartOfQuarterFromThisQuarter(now, 1), getEndOfQuarterFromThisQuarter(now, 1));
    case 'QUARTER_TO_DATE':
      return getInterval(getStartOfQuarter(now), getStartOfToday(now));
    case '6_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -5), getEndOfThisMonth(now));
    case '12_MONTHS':
      return getInterval(getStartOfMonthFromThisMonth(now, -11), getEndOfThisMonth(now));
    // Not compatible, but used by web first
    case '3_MONTHS_PRIOR':
      return getInterval(getStartOfMonthFromThisMonth(now, -3), getStartOfThisMonth(now));
    case '6_MONTHS_PRIOR':
      return getInterval(getStartOfMonthFromThisMonth(now, -6), getStartOfThisMonth(now));
    case '12_MONTHS_PRIOR':
      return getInterval(getStartOfMonthFromThisMonth(now, -12), getStartOfThisMonth(now));

    case 'CUSTOM':
      return getInterval(predefinedRange.startDate, predefinedRange.endDate);
  }
};

type PredefinedInterval = { value: string; endDate?: DateTime; startDate?: DateTime };

export const intervalForRange = (range: PredefinedInterval, now: DateTime) => {
  switch (range.value?.toLowerCase()) {
    // Investment ranges - start
    case '30 days':
      return constructPredefinedInterval({ value: '30_DAYS' }, now);
    case '60 days':
      return constructPredefinedInterval({ value: '60_DAYS' }, now);
    case '90 days':
      return constructPredefinedInterval({ value: '90_DAYS' }, now);
    case '6 months':
      return constructPredefinedInterval({ value: '6_MONTHS' }, now);
    case '365 days':
      return constructPredefinedInterval({ value: '365_DAYS' }, now);
    case 'next 12 months':
      return constructPredefinedInterval({ value: 'NEXT_365_DAYS' }, now);
    case 'last year':
      return constructPredefinedInterval({ value: 'LAST_YEAR' }, now);
    case 'this year':
      return constructPredefinedInterval({ value: 'YEAR_TO_DATE' }, now);
    // Investment ranges - end
    case 'last 30 days':
      return constructPredefinedInterval({ value: 'LAST_30_DAYS' }, now);
    case 'month to date':
      return constructPredefinedInterval({ value: 'MONTH_TO_DATE' }, now);
    case 'next 14 days':
      return constructPredefinedInterval({ value: 'NEXT_14_DAYS' }, now);
    case 'this month':
      return constructPredefinedInterval({ value: 'THIS_MONTH' }, now);
    case 'next month':
      return constructPredefinedInterval({ value: 'NEXT_MONTH' }, now);
    case 'next 30 days':
      return constructPredefinedInterval({ value: 'NEXT_30_DAYS' }, now);
    case 'next 60 days':
      return constructPredefinedInterval({ value: 'NEXT_60_DAYS' }, now);
    case 'this quarter':
      return constructPredefinedInterval({ value: 'THIS_QUARTER' }, now);
    case 'next quarter':
      return constructPredefinedInterval({ value: 'NEXT_QUARTER' }, now);
    case 'last month':
      return constructPredefinedInterval({ value: 'LAST_MONTH' }, now);
    case 'last 2 months':
      return constructPredefinedInterval({ value: 'LAST_2_MONTHS' }, now);
    case 'last 3 months':
      return constructPredefinedInterval({ value: 'LAST_3_MONTHS' }, now);
    case 'last 6 months':
      return constructPredefinedInterval({ value: 'LAST_6_MONTHS' }, now);
    case 'last 12 months':
      return constructPredefinedInterval({ value: 'LAST_12_MONTHS' }, now);
    case 'year to date':
      return constructPredefinedInterval({ value: 'YEAR_TO_DATE' }, now);
    case 'custom': {
      if (range.endDate && range.startDate) {
        return constructPredefinedInterval(
          { endDate: range.endDate, startDate: range.startDate, value: 'CUSTOM' },
          now,
        );
      }
      return constructPredefinedInterval({ value: 'LAST_30_DAYS' }, now);
    }
    default:
      return constructPredefinedInterval({ value: 'LAST_30_DAYS' }, now);
  }
};

// For Reference, Date Settings_Unused Supported by QMAC & QWIN

// This Month       - Start of Current Month to Start of Current Month + 1
// This Quarter     - Start of Current Quarter to Start of Current Quarter + 1
// This Year        - Start of Current Year to Start of Current Year + 1
//
// Last Week (Windows Only)
// Last Month       - Start of Current Month - 1 to Start of Current Month
// Last Quarter     - Start  of Current Quarter - 1 to Start of Current Quarter
// Last Year        - Start of Current Year - 1 to Start of Current Year
//

// Week to Date (Windows Only)
// Month to Date                       - Start of Current Month - Start of Today + 1
// Quarter to Date                     - Start of Current Quarter - Start of Today + 1
// Year to Date                        - Start of Current Year - Start of Today + 1
// Earliest to Date (Windows Only)     - Epoch to Start of Today + 1
// Custom to Date (Windows Only)

// Last 7 Days                         - Start of Current Day - 6 -> Start of Today + 1
// Last 14 Days (MAC Only)             - Start of Current Day - 13 -> Start of Today + 1
// Last 30 Days                        - Start of Current Day - 29 -> Start of Today + 1
// Last 60 Days (MAC Only)             - Start of Current Day - 60 -> Start of Today + 1
// Last 90 Days (MAC Only)             - Start of Current Day - 90 -> Start of Today + 1
// Last 12 Months                      - Start of Current Day - (12 months) -> Start of Today + 1

// Custom Range

// Daily              - Windows has bug, only shows today and last two days (assume it should show current and past 30 days
// Monthly            - Windows shows current month and past 12 months
// Yearly             - Windows shows current year and past N years
